/**
 * Product feature carousel
 *
 * A lightweight horizontal carousel of informational feature cards (image + heading + richtext):
 *  - swipes/scrolls natively (with scroll-snap) on touch devices
 *  - is draggable with the mouse, and can be advanced with prev/next arrows, on desktop
 *
 * Cards here are plain (not links), so unlike its sibling assets/collection-nav-carousel.js there
 * is no click-vs-drag ambiguity to resolve -- dragging never needs to suppress a click afterward.
 *
 * Loaded only on pages that render the "Product feature carousel" section (see
 * sections/product-feature-carousel.liquid), so it stays out of the theme's compiled
 * theme.js/custom.js bundles.
 */
(function () {
  if (window.customElements.get('product-feature-carousel')) {
    return;
  }

  var DRAG_THRESHOLD = 5; // px of movement before a pointer interaction counts as a drag

  class ProductFeatureCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.product-feature-carousel__scroller');
      this.items = Array.prototype.slice.call(this.querySelectorAll('.product-feature-carousel__item'));
      this.prevButton = this.querySelector('.product-feature-carousel__arrow--prev');
      this.nextButton = this.querySelector('.product-feature-carousel__arrow--next');

      if (!this.scroller || this.items.length === 0) {
        return;
      }

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._onDraggabilityChange = this._onDraggabilityChange.bind(this);

      this.addEventListener('prev-next:prev', this._onPrev);
      this.addEventListener('prev-next:next', this._onNext);

      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
      // `<img>` tags are natively draggable regardless of any wrapping element, so even without
      // an anchor in the card we still need to swallow a native image drag from stealing the
      // gesture mid-swipe.
      this.scroller.addEventListener('dragstart', this._onDragStart);
      window.addEventListener('resize', this._onResize);

      this._setupDraggability();
      this._updateArrows();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this.scroller.removeEventListener('dragstart', this._onDragStart);
      this._removeDragListeners();
    }

    _onDragStart(event) {
      event.preventDefault();
    }

    _direction() {
      return window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.direction === 'rtl'
        ? -1
        : 1;
    }

    _setupDraggability() {
      this._dragMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      if (this._dragMediaQuery.addEventListener) {
        this._dragMediaQuery.addEventListener('change', this._onDraggabilityChange);
      } else if (this._dragMediaQuery.addListener) {
        // Safari < 14
        this._dragMediaQuery.addListener(this._onDraggabilityChange);
      }

      if (this._dragMediaQuery.matches) {
        this._attachDragListeners();
      }
    }

    _onDraggabilityChange(event) {
      if (event.matches) {
        this._attachDragListeners();
      } else {
        this._removeDragListeners();
      }
    }

    _attachDragListeners() {
      if (this._dragListenersAttached) {
        return;
      }
      this._dragListenersAttached = true;
      this.scroller.addEventListener('pointerdown', this._onPointerDown);
    }

    _removeDragListeners() {
      this._dragListenersAttached = false;
      this.scroller.removeEventListener('pointerdown', this._onPointerDown);
    }

    _onPointerDown(event) {
      // Only the primary mouse button should start a drag.
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      // Tracking starts immediately, but this is provisional: until movement clears
      // DRAG_THRESHOLD (in _onPointerMove) nothing visually or behaviorally "drag-like" happens,
      // so a stationary press-and-release stays inert.
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.scroller.scrollLeft;

      this.scroller.addEventListener('pointermove', this._onPointerMove);
      this.scroller.addEventListener('pointerup', this._onPointerUp);
      this.scroller.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this.dragging) {
        return;
      }

      var deltaX = event.clientX - this.dragStartX;

      if (!this.dragMoved && Math.abs(deltaX) > DRAG_THRESHOLD) {
        // Threshold cleared for the first time this gesture: commit to "dragging" now, and
        // only now (cursor, scroll-snap suspension, and taking over the pointer so the drag
        // keeps tracking even if the cursor leaves the scroller).
        this.dragMoved = true;
        this.scroller.classList.add('is-dragging');
        this.scroller.setPointerCapture(event.pointerId);
      }

      if (this.dragMoved) {
        this.scroller.scrollLeft = this.dragStartScrollLeft - deltaX;
      }
    }

    _onPointerUp(event) {
      this.dragging = false;
      this.scroller.classList.remove('is-dragging');
      this.scroller.removeEventListener('pointermove', this._onPointerMove);
      this.scroller.removeEventListener('pointerup', this._onPointerUp);
      this.scroller.removeEventListener('pointercancel', this._onPointerUp);

      if (this.scroller.hasPointerCapture && this.scroller.hasPointerCapture(event.pointerId)) {
        this.scroller.releasePointerCapture(event.pointerId);
      }
    }

    _step() {
      var firstItem = this.items[0];
      var trackStyle = window.getComputedStyle(this.scroller.firstElementChild);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      var itemWidth = firstItem.getBoundingClientRect().width;
      var visibleCount = Math.max(1, Math.floor((this.scroller.clientWidth + gap) / (itemWidth + gap)));

      return visibleCount * (itemWidth + gap);
    }

    _onPrev() {
      this.scroller.scrollBy({ left: -this._step() * this._direction(), behavior: 'smooth' });
    }

    _onNext() {
      this.scroller.scrollBy({ left: this._step() * this._direction(), behavior: 'smooth' });
    }

    _onScroll() {
      if (this._scrollTicking) {
        return;
      }
      this._scrollTicking = true;
      window.requestAnimationFrame(() => {
        this._updateArrows();
        this._scrollTicking = false;
      });
    }

    _onResize() {
      this._updateArrows();
    }

    _updateArrows() {
      if (!this.prevButton || !this.nextButton) {
        return;
      }

      var maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth;
      var scrollLeft = Math.abs(this.scroller.scrollLeft);
      var tolerance = 1;

      this.prevButton.disabled = scrollLeft <= tolerance;
      this.nextButton.disabled = maxScrollLeft <= tolerance || scrollLeft >= maxScrollLeft - tolerance;
    }
  }

  window.customElements.define('product-feature-carousel', ProductFeatureCarousel);
})();
